import { useEffect, useState } from "react";
import { toast } from "components/ui/use-toast";

import { useGetTransactionReversalMadeQuery } from "@/services/transactionReversal";
import { useGetFilteredStoresQuery } from "@/services/store/store";

import { useFilterActions } from "../ducks/filter/hooks/action";
import { useFilterState } from "../ducks/filter/hooks/filterState";

import { columns } from "./components/columns";
import { DataTable } from "./components/data-table";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Load } from "components/load";
import { MainFilters } from "./components/filters/Mainfilters";

export default function ReversalMadeScreen() {
  const filterState = useFilterState();
  const { updateFilter } = useFilterActions();

  const [hasError, setHasError] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const {
    data: storesData,
    isLoading: isLoadingStores,
  } = useGetFilteredStoresQuery({
    perPage: 300,
    page: 1,
  });

  const advancedFilter = filterState?.filters?.advancedFilter;

  const dateStart = advancedFilter?.dateStart;
  const dateEnd = advancedFilter?.dateEnd;
  const operationType = advancedFilter?.operationType;
  const paymentType = advancedFilter?.paymentType;

  const hasRequiredFilters = !!(dateStart || dateEnd || operationType || paymentType);

  const { data, isFetching, error } = useGetTransactionReversalMadeQuery(
    {
      filters: {
        ...(advancedFilter?.store !== undefined && {
          storeId: advancedFilter.store,
        }),
        ...(advancedFilter?.dateStart !== undefined && {
          dateStart: advancedFilter.dateStart,
        }),
        ...(advancedFilter?.dateEnd !== undefined && {
          dateEnd: advancedFilter.dateEnd,
        }),
        ...(advancedFilter?.nsuNumber !== undefined && {
          authorizationNsu: advancedFilter.nsuNumber,
        }),
        ...(advancedFilter?.operationType !== undefined && {
          operationType: advancedFilter.operationType,
        }),
        ...(advancedFilter?.paymentType !== undefined && {
          paymentType: advancedFilter.paymentType,
        }),
        ...(advancedFilter?.paymentValue !== undefined && {
          paymentValue: advancedFilter.paymentValue,
        }),
        ...(advancedFilter?.user !== undefined && {
          user: advancedFilter.user,
        }),
        page: filterState?.filters?.meta?.page ?? 1,
        perPage: filterState?.filters?.meta?.perPage ?? 10,
      },
    },
    {
      skip: !hasRequiredFilters || hasError || !shouldFetch,
    },
  );

  useEffect(() => {
    updateFilter({
      key: "meta",
      value: { page: 1 },
    });
    
    setHasError(false);

    if (hasRequiredFilters) {
      setShouldFetch(true);
    }
  }, [advancedFilter]);

  useEffect(() => {
    if (!error) return;

    setHasError(true);

    toast({
      variant: "destructive",
      description:
        "Ocorreu um erro inesperado ao buscar as movimentações. Tente novamente mais tarde.",
      duration: 5000,
    });
  }, [error]);

  const handleClearData = () => {
    setShouldFetch(false);
  };

  const tableData = shouldFetch ? ((data as any)?.data ?? []) : [];
  const hasData = shouldFetch && Array.isArray(tableData) && tableData.length > 0;

  return (
    <PageLayout>
      <PageTitle
        title="Estornos Realizados"
        description="Visualize o histórico de estornos realizados"
      />
      <ContentContainer>
        {isLoadingStores ? (
          <div className="flex items-center justify-center h-64">
            <Load />
          </div>
        ) : (
          <>
            <MainFilters
              storesData={storesData?.data || []}
              hasData={hasData}
              isLoading={isFetching}
              onClearData={handleClearData}
            />

            <div className="relative">
              {isFetching && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <svg
                    className="animate-spin h-12 w-12 text-primary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
              <DataTable
                data={tableData}
                columns={columns}
                meta={(data as any)?.meta}
              />
            </div>
          </>
        )}
      </ContentContainer>
    </PageLayout>
  );
}